import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle, Circle, Flag, Clock, BookmarkCheck, Target } from "lucide-react";
import { cn } from "@/lib/utils";

export interface QuestionStatus {
  answered: boolean;
  correct?: boolean;
  flagged: boolean;
  bookmarked: boolean;
  timeSpent?: number;
}

interface MCQNavigationPaletteProps {
  totalQuestions: number;
  currentQuestion: number;
  questionStatuses: QuestionStatus[];
  onQuestionSelect: (index: number) => void;
  className?: string;
  compact?: boolean;
}

export const MCQNavigationPalette = ({
  totalQuestions,
  currentQuestion,
  questionStatuses,
  onQuestionSelect,
  className,
  compact = false
}: MCQNavigationPaletteProps) => {
  const answeredCount = questionStatuses.filter(q => q.answered).length;
  const correctCount = questionStatuses.filter(q => q.correct === true).length;
  const flaggedCount = questionStatuses.filter(q => q.flagged).length;
  const bookmarkedCount = questionStatuses.filter(q => q.bookmarked).length;
  const unansweredCount = totalQuestions - answeredCount;

  const getQuestionStatusColor = (index: number) => {
    const status = questionStatuses[index];
    const isCurrent = index === currentQuestion;
    
    if (isCurrent) return "bg-primary text-primary-foreground border-primary shadow-lg scale-110";
    if (status?.correct === true) return "bg-accent/20 text-accent border-accent hover:bg-accent/30";
    if (status?.correct === false) return "bg-destructive/20 text-destructive border-destructive hover:bg-destructive/30";
    if (status?.answered) return "bg-muted text-foreground border-muted-foreground hover:bg-muted/80";
    return "bg-card text-muted-foreground border-muted hover:bg-muted/50";
  };

  const getStatusIndicators = (index: number) => {
    const status = questionStatuses[index];
    const indicators = [];
    
    if (status?.flagged) {
      indicators.push(
        <Flag key="flag" className="h-2.5 w-2.5 fill-current text-orange-500" />
      );
    }
    
    if (status?.bookmarked) {
      indicators.push(
        <BookmarkCheck key="bookmark" className="h-2.5 w-2.5 fill-current text-blue-500" />
      );
    }
    
    return indicators;
  };

  if (compact) {
    return (
      <div className={cn("space-y-3", className)}>
        {/* Compact Stats */}
        <div className="flex gap-2 text-xs">
          <Badge variant="outline" className="bg-accent/10 text-accent border-accent">
            ✓ {correctCount}
          </Badge>
          <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive">
            ✗ {answeredCount - correctCount}
          </Badge>
          <Badge variant="outline" className="text-muted-foreground">
            {unansweredCount} left
          </Badge>
        </div>
        
        {/* Horizontal Question Grid */}
        <ScrollArea className="w-full">
          <div className="flex gap-1 pb-2">
            {Array.from({ length: totalQuestions }, (_, index) => {
              const indicators = getStatusIndicators(index);
              return (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  className={cn(
                    "relative h-8 w-8 p-0 text-xs font-medium shrink-0 transition-all duration-200",
                    getQuestionStatusColor(index)
                  )}
                  onClick={() => onQuestionSelect(index)}
                >
                  {index + 1}
                  
                  {/* Status indicators */}
                  {indicators.length > 0 && (
                    <div className="absolute -top-1 -right-1 flex gap-0.5">
                      {indicators}
                    </div>
                  )}
                </Button>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <Card className={cn("medical-card w-80", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Target className="h-5 w-5 text-primary" />
          Questions Overview
        </CardTitle>
        
        {/* Status Summary */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <Badge variant="outline" className="bg-accent/10 text-accent border-accent justify-center">
            {correctCount} Correct
          </Badge>
          <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive justify-center">
            {answeredCount - correctCount} Wrong
          </Badge>
          <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-700 justify-center">
            {flaggedCount} Flagged
          </Badge>
          <Badge variant="outline" className="text-muted-foreground justify-center">
            {unansweredCount} Left
          </Badge>
        </div>

        {/* Progress indicator */}
        <div className="text-center p-2 bg-muted/30 rounded-lg border">
          <div className="text-xs text-muted-foreground">Progress</div>
          <div className="text-lg font-bold text-primary">
            {answeredCount}/{totalQuestions}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <ScrollArea className="h-64">
          <div className="grid grid-cols-5 gap-2">
            {Array.from({ length: totalQuestions }, (_, index) => {
              const indicators = getStatusIndicators(index);
              return (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  className={cn(
                    "relative h-10 w-10 p-0 transition-all duration-200",
                    getQuestionStatusColor(index)
                  )}
                  onClick={() => onQuestionSelect(index)}
                >
                  <span className="text-xs font-medium">{index + 1}</span>
                  
                  {/* Status indicators */}
                  {indicators.length > 0 && (
                    <div className="absolute -top-1 -right-1 flex gap-0.5">
                      {indicators}
                    </div>
                  )}
                </Button>
              );
            })}
          </div>
        </ScrollArea>

        {/* Legend */}
        <div className="mt-4 space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary rounded border"></div>
            <span>Current</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-accent/30 rounded border border-accent"></div>
            <span>Correct</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-destructive/30 rounded border border-destructive"></div>
            <span>Incorrect</span>
          </div>
          <div className="flex items-center gap-2">
            <Flag className="h-3 w-3 text-orange-500" />
            <span>Flagged</span>
          </div>
          <div className="flex items-center gap-2">
            <BookmarkCheck className="h-3 w-3 text-blue-500" />
            <span>Bookmarked</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};